/**
 * CPR — Close Price Reversal. THE SweeterTrade screener's CprUp / CprDown
 * signal (aka the protected "JAS CPR" TradingView study), as MarketPulse's own
 * standalone indicator + criteria family. Deliberately SEPARATE from the John
 * Person family even though the engine is the same `computePps` math — user
 * call 2026-07-14: keep CPR and the Person Signal/Trend as two independent
 * indicators/criteria, don't mix the branding.
 *
 * Rule per bar (pivot = (H+L+C)/3; MA1 = MA(pivot, ma1Len), default 1 = the
 * pivot itself; MA2 = MA(pivot, ma2Len), default 3; MA = SMA by default — the
 * screener's math, verified bar-exact — 300/300 bars, all 63 flips — against a
 * JAS CPR daily export of XOM):
 *
 *   Buy  (ALL true): MA1 > MA2, close > open, close > MA1, close > MA2 + latch
 *   Sell (ALL true): the mirror
 *
 * close > MA1 is close > (H+L)/2 — an upper-half close — so a Buy bar is
 * always a green, close-up, upper-half-of-range bar, the first since the last
 * Sell. The latch arms on the OPPOSITE alignment and is consumed on fire, so
 * each side fires once per regime and signals STRICTLY ALTERNATE.
 *
 *   flip = +1 on a Buy bar / -1 on a Sell bar / 0 otherwise — "CprUp/CprDown
 *          fired today" is flip on the latest bar.
 *   cond = the last flip latched forward (+1 bull / -1 bear, 0 before the
 *          first flip) — the regime between signals.
 *
 * One `computeCpr` feeds the scanner/watchlist state, the standalone CPR chart
 * arrows, and the MDA Dots "CPR" lane, so they can't drift.
 *
 * History: until 2026-07-14 this file held three reverse-engineered
 * APPROXIMATION modes (regime-gap, pending-confirm, deep-swing SAR) built
 * before the exact rule was identified. They are gone — one exact engine now.
 * Legacy persisted params (mode / gapPct / waitBars / histConfirm / halfBar /
 * peakAbs / peakPct) are simply ignored.
 */
import { familyGroup } from "../indicatorGroups";
import { TF_DW, TF_PARAM } from "./timeframe";
import type { OHLCV, IndicatorParams, SharedIndicatorDef, SharedParam } from "../types";
import { ageSeries } from "./age";
import { computePps, latchTrend } from "./pps";
import { resampleOHLC, htIndexPerDay } from "./weekly";

const num = (v: unknown, d: number): number => {
  if (typeof v === "number" && Number.isFinite(v)) return v;
  if (typeof v === "string" && v.trim() !== "" && Number.isFinite(Number(v))) return Number(v);
  return d;
};

/** Absent → SMA (the screener's default); 0 / "0" / "ema" selects EMA. */
const cprMaType = (v: unknown): "ema" | "sma" =>
  v === 0 || v === "0" || v === "ema" ? "ema" : "sma";

export type CprParts = {
  /** Latched regime: +1 bull/long, -1 bear/short, 0 before the first flip. */
  cond: (number | null)[];
  /** Flip bar: +1 on Buy, -1 on Sell, 0 otherwise. */
  flip: (number | null)[];
};

export function computeCpr(data: OHLCV, params: IndicatorParams = {}): CprParts {
  const { opens, highs, lows, closes } = data;
  const n = closes.length;
  if (!opens || !highs || !lows || n === 0) {
    return { cond: new Array(n).fill(0), flip: new Array(n).fill(0) };
  }
  const ma1Len = Math.max(1, Math.round(num(params.ma1Len, 1)));
  const ma2Len = Math.max(1, Math.round(num(params.ma2Len, 3)));
  const flip = computePps(opens, highs, lows, closes, ma1Len, ma2Len, cprMaType(params.maType));
  return { cond: latchTrend(flip), flip };
}

export const CPR_PARAMS: ReadonlyArray<SharedParam> = [
  { id: "ma1Len", label: "Pivot MA1", kind: "number", default: 1, min: 1, max: 50, step: 1 },
  { id: "ma2Len", label: "Pivot MA2", kind: "number", default: 3, min: 1, max: 50, step: 1 },
  // SMA default = the SweeterTrade CprUp/CprDown / "JAS CPR" variant.
  { id: "maType", label: "MA type", kind: "select", default: 1, choices: [{ value: 0, label: "EMA" }, { value: 1, label: "SMA" }] },
];

const CPR_LS = { pos: "Buy", neg: "Sell", zero: "—" } as const;

// CPR folds Daily + Weekly into one def per concept + a Timeframe select
// (default Daily). Daily = computeCpr; Weekly = the ISO-week series (hoisted
// below). Weekly stays instant via the tf-resolver (cpr_state_w / cpr_signal_w /
// cpr_w_age). Trend = regime (cond); Signal = flip dot; Age = bars/weeks held.
export const CPR: SharedIndicatorDef = {
  id: "mpp-cpr",
  label: "Afiat - Pivot Close Reversal",
  group: familyGroup("cpr"),
  kind: "state",
  format: "state",
  stateLabels: { pos: "Bull", neg: "Bear", zero: "—" },
  panelKey: "cpr_state_d",
  params: [TF_DW, ...CPR_PARAMS],
  computeSeries: (d, p) => (p[TF_PARAM] === 1 ? cprWeeklyCondSeries(d, p) : computeCpr(d, p).cond),
};

/** Weekly CPR regime projected onto daily bars (each day → its ISO-week's
 *  latched cond). cond/flip are OHLC-only so weekly volume isn't needed. */
export function cprWeeklyCondSeries(data: OHLCV, params: IndicatorParams = {}): (number | null)[] {
  const { times, opens, highs, lows, closes } = data;
  const n = closes.length;
  if (!times || times.length !== n || !opens || !highs || !lows) return new Array(n).fill(null);
  const wk = resampleOHLC(times, opens, highs, lows, closes, "week");
  if (wk.c.length < 8) return new Array(n).fill(null);
  const { cond } = computeCpr(
    { opens: wk.o, highs: wk.h, lows: wk.l, closes: wk.c, volumes: new Array(wk.c.length).fill(0) },
    params,
  );
  const wkIdx = htIndexPerDay(times, wk.t);
  return times.map((_, i) => {
    const k = wkIdx[i]!;
    return k >= 0 && k < cond.length ? cond[k]! : null;
  });
}

/** CPR Signal — the CPR flip (the dot) on the latest bar: +1 Buy / -1 Sell / 0
 *  none. Matches the CPR Signal criteria (cpr_signal_d / cpr_signal_w). */
export const CPR_DAILY: SharedIndicatorDef = {
  id: "mpp-cpr-daily",
  label: "Afiat - Pivot Close Reversal",
  group: familyGroup("cpr"),
  kind: "state",
  format: "state",
  stateLabels: CPR_LS,
  panelKey: "cpr_signal_d",
  params: [TF_DW, ...CPR_PARAMS],
  computeSeries: (d, p) => (p[TF_PARAM] === 1 ? cprWeeklySignalSeries(d, p) : computeCpr(d, p).flip),
};

/** Weekly CPR flip (the dot) projected onto daily bars — +1 Buy / -1 Sell / 0
 *  on each ISO week. */
export function cprWeeklySignalSeries(data: OHLCV, params: IndicatorParams = {}): (number | null)[] {
  const { times, opens, highs, lows, closes } = data;
  const n = closes.length;
  if (!times || times.length !== n || !opens || !highs || !lows) return new Array(n).fill(null);
  const wk = resampleOHLC(times, opens, highs, lows, closes, "week");
  if (wk.c.length < 8) return new Array(n).fill(null);
  const { flip } = computeCpr(
    { opens: wk.o, highs: wk.h, lows: wk.l, closes: wk.c, volumes: new Array(wk.c.length).fill(0) },
    params,
  );
  const wkIdx = htIndexPerDay(times, wk.t);
  return times.map((_, i) => {
    const k = wkIdx[i]!;
    return k >= 0 && k < flip.length ? flip[k]! : null;
  });
}

/** Weeks the weekly CPR regime has held, projected onto daily bars. */
export function cprWeeklyAgeSeries(data: OHLCV, params: IndicatorParams = {}): (number | null)[] {
  const { times, opens, highs, lows, closes } = data;
  const n = closes.length;
  if (!times || times.length !== n || !opens || !highs || !lows) return new Array(n).fill(null);
  const wk = resampleOHLC(times, opens, highs, lows, closes, "week");
  if (wk.c.length < 8) return new Array(n).fill(null);
  const { cond } = computeCpr(
    { opens: wk.o, highs: wk.h, lows: wk.l, closes: wk.c, volumes: new Array(wk.c.length).fill(0) },
    params,
  );
  const wAge = ageSeries(cond, "state");
  const wkIdx = htIndexPerDay(times, wk.t);
  return times.map((_, i) => {
    const k = wkIdx[i]!;
    return k >= 0 && k < wAge.length ? wAge[k]! : null;
  });
}

/** Bars/weeks the CPR regime has held (age of cond). Weekly reads cpr_w_age. */
export const CPR_AGE: SharedIndicatorDef = {
  id: "mpp-cpr-age",
  label: "Afiat - Pivot Close Reversal Age",
  group: familyGroup("cpr"),
  kind: "age",
  format: "age",
  panelKey: "cpr_age", // bars since the last CPR flip (worker-baked)
  params: [TF_DW, ...CPR_PARAMS],
  computeSeries: (d, p) =>
    p[TF_PARAM] === 1 ? cprWeeklyAgeSeries(d, p) : ageSeries(computeCpr(d, p).cond, "state"),
};

export const CPR_INDICATORS: ReadonlyArray<SharedIndicatorDef> = [CPR, CPR_DAILY, CPR_AGE];
